import React from "react";
import { Helmet } from "react-helmet-async";
import { useStore } from "../context/StoreContext";
import BLOG_POSTS from "../data/blogPosts";
import "./Blog.css";

const SITE = "https://cityshopingcenter.com";
const BASE_NAME = "City Shopping Center & Dulha House";

/* Deterministic date formatting.
   toLocaleDateString() depends on the environment's locale/timezone, so it
   produced a different string during the react-snap prerender than in the
   browser. That mismatch was causing React hydration errors #418/#423.
   Formatting manually guarantees both sides render identical text. */
const MONTHS = [
  "January", "February", "March", "April", "May", "June",
  "July", "August", "September", "October", "November", "December",
];

const fmtDate = (iso) => {
  try {
    const parts = String(iso).split("-");
    if (parts.length !== 3) return iso;
    const y = parseInt(parts[0], 10);
    const m = parseInt(parts[1], 10);
    const d = parseInt(parts[2], 10);
    if (!y || !m || !d || m < 1 || m > 12) return iso;
    return d + " " + MONTHS[m - 1] + " " + y;
  } catch {
    return iso;
  }
};

export default function BlogPost() {
  const { navigate, viewBlogPost, goShop, selectedPostSlug } = useStore();

  const post = BLOG_POSTS.find((p) => p.slug === selectedPostSlug) || null;
  const others = BLOG_POSTS.filter((p) => p.slug !== selectedPostSlug).slice(0, 3);

  if (!post) {
    return (
      <div className="blog-page">
        <Helmet>
          <title>{"Article Not Found — " + BASE_NAME}</title>
          <meta name="robots" content="noindex" />
        </Helmet>
        <div className="page-header" data-echo="BLOG">
          <h1>ARTICLE NOT FOUND</h1>
          <p>This article may have been moved or removed</p>
        </div>
        <div className="blogpost-notfound">
          <button className="btn-primary" onClick={() => navigate("blog")}>← Back to Blog</button>
        </div>
      </div>
    );
  }

  const url = `${SITE}/blog/${post.slug}`;
  const jsonLd = {
    "@context": "https://schema.org",
    "@type": "BlogPosting",
    headline: post.title,
    description: post.excerpt,
    url,
    datePublished: post.date,
    author: { "@type": "Organization", name: BASE_NAME },
    publisher: {
      "@type": "Organization",
      name: BASE_NAME,
      logo: { "@type": "ImageObject", url: `${SITE}/logo.jpeg` },
    },
    mainEntityOfPage: { "@type": "WebPage", "@id": url },
  };

  return (
    <div className="blog-page">
      <Helmet>
        <title>{post.title + " | Artificial Jewellery Guide Pakistan"}</title>
        <meta name="description" content={post.excerpt} />
        <link rel="canonical" href={url} />
        <meta property="og:type" content="article" />
        <meta property="og:title" content={post.title} />
        <meta property="og:description" content={post.excerpt} />
        <meta property="og:url" content={url} />
        <meta property="og:image" content={`${SITE}/logo.jpeg`} />
        <script type="application/ld+json">{JSON.stringify(jsonLd)}</script>
      </Helmet>

      <div className="blogpost-wrap">
        <button className="blogpost-back" onClick={() => navigate("blog")}>← All Articles</button>

        <div className="blog-card-meta">
          <span>{fmtDate(post.date)}</span>
          <span className="blog-card-dot">·</span>
          <span>{post.readTime}</span>
        </div>

        <h1 className="blogpost-title">{post.title}</h1>

        {post.sections.map((sec, i) => (
          <section key={i} className="blogpost-section">
            {sec.heading && <h2>{sec.heading}</h2>}
            {sec.paragraphs.map((para, j) => (
              <p key={j}>{para}</p>
            ))}
          </section>
        ))}

        <div className="blogpost-cta">
          <p>Looking for jewelry that matches this guide?</p>
          <button className="btn-primary" onClick={() => goShop("All")}>Browse Our Collection</button>
        </div>

        {others.length > 0 && (
          <div className="blogpost-more">
            <h3>More Articles</h3>
            {others.map((p) => (
              <button key={p.slug} className="blogpost-more-link" onClick={() => viewBlogPost(p.slug)}>
                {p.title} →
              </button>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
